/**
 * Alpine x-validate directive — blur/focus validation for required fields.
 *
 * Usage:
 *   <input required x-validate>                          — default message
 *   <input required x-validate="'Email is required'">    — custom message
 *
 * For hidden inputs inside dropdown components, the directive listens
 * on the sibling .dropdown__trigger button instead.
 */

const SERVER_ERROR_SELECTORS = [
  '.text-md-error',
  '.text-field__error-text',
  '.dropdown__error-text',
  '.auth__error',
];

/**
 * Check if a sibling or nearby element already shows a server-rendered error.
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function hasServerError(el) {
  const parent = el.closest('[data-component]') || el.parentElement;
  if (!parent) return false;

  for (const selector of SERVER_ERROR_SELECTORS) {
    if (parent.querySelector(selector)) return true;
  }
  return false;
}

/**
 * Check whether the element is visible in the DOM.
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isVisible(el) {
  return el.offsetParent !== null || el.getClientRects().length > 0;
}

/**
 * Determine if a field's value is "empty" for validation purposes.
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isEmpty(el) {
  if (el.type === 'hidden') return !el.value;
  if (el.type === 'file') return el.files.length === 0;
  return el.value.trim() === '';
}

/**
 * Inject an error message element after the target.
 *
 * @param {HTMLElement} target  — element to insert error after
 * @param {string} message
 */
function showError(target, message) {
  const existing = target.parentElement?.querySelector('[data-validate-error]');
  if (existing) return;

  const p = document.createElement('p');
  p.className = 'validate-error-text';
  p.setAttribute('data-validate-error', '');
  p.textContent = message;

  target.setAttribute('data-validate-invalid', '');

  const insertAfter =
    target.closest('[data-component="text-field"]') ||
    target.closest('[data-component="dropdown"]') ||
    target.parentElement;

  insertAfter.appendChild(p);
}

/**
 * Remove the injected error message and invalid state.
 *
 * @param {HTMLElement} target
 */
function clearError(target) {
  target.removeAttribute('data-validate-invalid');

  const container =
    target.closest('[data-component="text-field"]') ||
    target.closest('[data-component="dropdown"]') ||
    target.parentElement;

  const error = container?.querySelector('[data-validate-error]');
  if (error) error.remove();
}

/**
 * Register the x-validate directive on the given Alpine instance.
 *
 * @param {object} Alpine
 */
export default function registerValidateDirective(Alpine) {
  Alpine.directive('validate', (el, { expression }, { evaluate, cleanup }) => {
    const message = expression
      ? evaluate(expression)
      : 'This field is required';

    const isDropdownHidden =
      el.type === 'hidden' &&
      el.closest('[data-component="dropdown"]') !== null;

    const blurTarget = isDropdownHidden
      ? el
          .closest('[data-component="dropdown"]')
          ?.querySelector('.dropdown__trigger')
      : el;

    if (!blurTarget) return;

    function onBlur() {
      if (!el.hasAttribute('required')) return;
      if (!isVisible(blurTarget)) return;
      if (hasServerError(el)) return;
      if (isEmpty(el)) {
        showError(el, message);
      }
    }

    function onFocus() {
      clearError(el);
    }

    blurTarget.addEventListener('blur', onBlur);
    blurTarget.addEventListener('focus', onFocus);

    cleanup(() => {
      blurTarget.removeEventListener('blur', onBlur);
      blurTarget.removeEventListener('focus', onFocus);
      clearError(el);
    });
  });
}
